import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export default function useHibidEmailTools({
  apiBaseUrl,
  routes,
  apiFetchJson,
  orgReady,
  primary,
  subscriptionId,
  subscriptionName,
  selectedAuctionId,
  selectedAuctionLabel,
  allFilteredUsers,
  selectedFilteredUsers,
  chargebackFilteredAuctionRegs,
  normalize,
  isEmail,
  toAbsInt,
  toastOpen,
  sanitizeUrl,
  resolveEmailFromName,
  emptyEmailDesign,
  defaultShippingPolicyText,
  setUserRows,
  setAuctionRegs,
  invalidateUsersCache,
  invalidateAuctionRegsCache,
  setChargebackEmailFulfillmentFilter,
  setChargebackEmailAlreadySentFilter,
  setChargebackEmailWonLotFilter,
}) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailPreviewBusy, setEmailPreviewBusy] = useState(false);
  const [emailTab, setEmailTab] = useState("compose");
  const [emailMode, setEmailMode] = useState("users");
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailRecipientScope, setEmailRecipientScope] = useState("selected");
  const [emailRecipientFirstN] = useState(100);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailFromName, setEmailFromName] = useState("");
  const [emailHtml, setEmailHtml] = useState("");
  const [emailText, setEmailText] = useState("");
  const [emailPreviewHtml, setEmailPreviewHtml] = useState("");
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplatesBusy, setEmailTemplatesBusy] = useState(false);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState("");
  const [selectedEmailTemplateName, setSelectedEmailTemplateName] = useState("");
  const [createEmailTemplateOpen, setCreateEmailTemplateOpen] = useState(false);
  const [createEmailTemplateName, setCreateEmailTemplateName] = useState("");
  const [createEmailTemplateBaseId, setCreateEmailTemplateBaseId] = useState("");
  const [shippingTextOpen, setShippingTextOpen] = useState(false);
  const [shippingTextSaving, setShippingTextSaving] = useState(false);
  const [shippingTextDraft, setShippingTextDraft] = useState(defaultShippingPolicyText);
  const [shippingTextSaved, setShippingTextSaved] = useState(defaultShippingPolicyText);
  const [emailUnlayerDesign, setEmailUnlayerDesign] = useState(emptyEmailDesign);
  const [pendingEmailDesign, setPendingEmailDesign] = useState(emptyEmailDesign);
  const [emailEditorReady, setEmailEditorReady] = useState(false);
  const [emailAutoSaveStatus, setEmailAutoSaveStatus] = useState("");

  const emailEditorRef = useRef(null);
  const emailAutoSaveTimerRef = useRef(null);
  const skipEmailDesignAutoSaveRef = useRef(false);
  const emailSkipAutoSaveRef = useRef(false);

  const LS_SETTINGS_KEY = "marketingEmail:settings:v2";
  const DEFAULT_SETTINGS = {
    companyName: "Listernaut",
    companyEmail: "vrushab1998@gmail.com",
    websiteLink: "https://listernaut.com/",
    brandColor: "#333999",
    logoUrl: "https://storage.googleapis.com/listalotimages/emailtemplates/logo.png",
    tagline: "Important update",
  };
  const [settings] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_SETTINGS_KEY)) || null;
      return saved ? { ...DEFAULT_SETTINGS, ...saved } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_SETTINGS_KEY, JSON.stringify(settings));
    } catch { }
  }, [settings]);

  const defaultEmailFromName = String(settings.companyName || "Listernaut").trim() || "Listernaut";

  useEffect(() => {
    setEmailFromName((prev) => (String(prev || "").trim() ? prev : defaultEmailFromName));
  }, [defaultEmailFromName]);

  const shippingTextDirty = String(shippingTextDraft || "").trim() !== String(shippingTextSaved || "").trim();

  const buildFooterHtml = useCallback(() => {
    const brand = String(settings.brandColor || primary).trim() || primary;
    const companyName = String(settings.companyName || "Listernaut").trim() || "Listernaut";
    const companyEmail = String(settings.companyEmail || "vrushab1998@gmail.com").trim() || "vrushab1998@gmail.com";
    const website = sanitizeUrl(String(settings.websiteLink || "https://listernaut.com/").trim() || "https://listernaut.com/");
    const unsubscribeUrl = "https://explorer.listernaut.com/unsubscribe?email={{email}}";

    return `
      <hr style="border:none;border-top:1px solid #e5e7eb;margin:18px 0"/>
      <div style="font-family:Arial,Helvetica,sans-serif;font-size:13px;line-height:1.5;text-align:center;background:#f3f4f6;border:1px solid #e5e7eb;border-radius:12px;padding:14px 14px 12px;">
        <div style="font-weight:800;color:${brand};font-size:14px;">${companyName}</div>
        <div style="margin-top:6px;">
          <a href="mailto:${companyEmail}" style="color:${brand};text-decoration:none;">${companyEmail}</a>
        </div>
        <div style="margin-top:10px;font-size:12px;">
          <a href="${website}" target="_blank" rel="noreferrer" style="color:${brand};text-decoration:underline;">About us</a>
          <span style="display:inline-block;width:10px;"></span>
          <span style="color:#9ca3af;">|</span>
          <span style="display:inline-block;width:10px;"></span>
          <a href="${unsubscribeUrl}" style="color:${brand};text-decoration:underline;">Unsubscribe</a>
        </div>
        <div style="margin-top:10px;font-size:11px;color:#6b7280;">Questions? Reply to this email.</div>
      </div>
    `;
  }, [primary, sanitizeUrl, settings]);

  const wrapEmailHtml = useCallback((bodyHtml) => {
    const brand = String(settings.brandColor || primary).trim() || primary;
    const logo = String(settings.logoUrl || "").trim();
    const tag = String(settings.tagline || "Important update").trim() || "Important update";
    const cname = String(settings.companyName || "Update").trim() || "Update";

    return `<!doctype html>
<html>
  <body style="margin:0;padding:0;background:#f3f5f9;font-family:Arial,Helvetica,sans-serif;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f3f5f9;">
      <tr>
        <td align="center" style="padding:24px;">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0"
                 style="max-width:680px;background:#ffffff;border-radius:16px;border:1px solid #e6e8f0;overflow:hidden;">
            <tr>
              <td align="center" style="background:${brand};padding:20px 24px;">
                ${logo
        ? `<img src="${logo}" alt="Logo" style="height:40px;background:#fff;padding:8px;border-radius:6px;display:block;" />`
        : `<div style="font-weight:800;color:#fff;font-size:18px;">${cname}</div>`
      }
                <div style="color:rgba(255,255,255,0.85);font-size:13px;margin-top:8px;">${tag}</div>
              </td>
            </tr>
            <tr>
              <td style="padding:30px;color:#111827;font-size:14px;line-height:1.6;text-align:left;">
                ${bodyHtml || ""}
                ${buildFooterHtml()}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
  }, [buildFooterHtml, primary, settings]);

  const buildDefaultAuctionUrl = useCallback(() => {
    if (!selectedAuctionId) return "";
    return "https://hibid.com/";
  }, [selectedAuctionId]);

  const getComposedEmailHtml = useCallback(() => {
    const raw = String(emailHtml || "").trim();
    if (!raw) return "";
    return /<html[\s>]/i.test(raw) ? raw : wrapEmailHtml(raw);
  }, [emailHtml, wrapEmailHtml]);

  const computeEmailRecipients = useCallback(() => {
    const normalizeRecipient = (recipient = {}) => {
      const email = normalize(recipient?.email);
      if (!isEmail(email)) return null;
      return { ...recipient, email };
    };

    if (emailRecipientScope === "selected") {
      return (emailRecipients || []).map(normalizeRecipient).filter(Boolean);
    }

    if (emailMode === "users") {
      return [];
    }

    const n = Math.max(1, toAbsInt(emailRecipientFirstN || 0));

    if (emailMode === "auction") {
      const base = Array.isArray(chargebackFilteredAuctionRegs) ? chargebackFilteredAuctionRegs : [];
      const slice = emailRecipientScope === "all" ? base : base.slice(0, n);

      return slice
        .map((row) => {
          const email = normalize(row?.bidderEmail || row?.email);
          if (!isEmail(email)) return null;
          return {
            email,
            bidderName: row?.bidderName || "",
            username: row?.username || "",
            auctionId: selectedAuctionId || row?.auctionId || "",
            auctionName: selectedAuctionLabel || row?.auctionName || "",
            registrationId: String(row?.registrationId || row?.id || "").trim(),
          };
        })
        .filter(Boolean);
    }

    return [];
  }, [
    chargebackFilteredAuctionRegs,
    emailMode,
    emailRecipientFirstN,
    emailRecipientScope,
    emailRecipients,
    isEmail,
    normalize,
    selectedAuctionId,
    selectedAuctionLabel,
    toAbsInt,
  ]);

  const emailRecipientsComputed = useMemo(() => computeEmailRecipients(), [computeEmailRecipients]);
  const emailSingleUserTarget =
    emailMode === "users" && emailRecipientScope === "selected" && emailRecipientsComputed.length === 1
      ? normalize(emailRecipientsComputed?.[0]?.email || "")
      : "";
  const emailRecipientTotalForDisplay = emailRecipientsComputed.length;

  const loadEmailDesignIntoBuilder = useCallback((design) => {
    const editor = emailEditorRef.current?.editor;
    if (!editor || typeof editor.loadDesign !== "function" || !design) return;
    try {
      skipEmailDesignAutoSaveRef.current = true;
      editor.loadDesign(design);
      setTimeout(() => {
        skipEmailDesignAutoSaveRef.current = false;
      }, 3000);
    } catch {
      skipEmailDesignAutoSaveRef.current = false;
    }
  }, []);

  const handleEmailEditorReady = useCallback(() => {
    setEmailEditorReady(true);
    loadEmailDesignIntoBuilder(pendingEmailDesign || emailUnlayerDesign || emptyEmailDesign);
  }, [emailUnlayerDesign, emptyEmailDesign, loadEmailDesignIntoBuilder, pendingEmailDesign]);

  useEffect(() => {
    if (!emailEditorReady) return;
    if (!pendingEmailDesign) return;
    loadEmailDesignIntoBuilder(pendingEmailDesign);
    setPendingEmailDesign(null);
  }, [emailEditorReady, loadEmailDesignIntoBuilder, pendingEmailDesign]);

  const exportEmailFromBuilder = useCallback(async () => {
    const editor = emailEditorRef.current?.editor;
    if (!editor || typeof editor.exportHtml !== "function") {
      return { html: getComposedEmailHtml(), design: emailUnlayerDesign };
    }
    return new Promise((resolve) => {
      editor.exportHtml((data) => {
        const rawHtml = String(data?.html || "").trim();
        const exportedDesign = data?.design || null;
        const exportedHtml = rawHtml.length > 40 ? rawHtml : getComposedEmailHtml();
        if (exportedDesign) setEmailUnlayerDesign(exportedDesign);
        if (rawHtml.length > 40) setEmailHtml(rawHtml);
        resolve({ html: exportedHtml, design: exportedDesign || emailUnlayerDesign });
      });
    });
  }, [emailUnlayerDesign, getComposedEmailHtml]);

  const doEmailAutoSaveToDb = useCallback(async () => {
    const templateId = String(selectedEmailTemplateId || "").trim();
    if (!templateId || !orgReady) return;

    const editor = emailEditorRef.current?.editor;
    let design = emailUnlayerDesign;
    let html = emailHtml;

    if (editor && typeof editor.exportHtml === "function") {
      const data = await new Promise((resolve) => editor.exportHtml((exported) => resolve(exported)));
      const freshDesign = data?.design ?? null;
      const freshHtml = String(data?.html || "");
      const hasRealContent = freshHtml.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().length > 50;
      if (hasRealContent) {
        design = freshDesign ?? design;
        html = freshHtml;
        if (freshDesign) setEmailUnlayerDesign(freshDesign);
        setEmailHtml(freshHtml);
      }
    }

    const textContent = String(html || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
    if (textContent.length < 50) {
      setEmailAutoSaveStatus("");
      return;
    }

    setEmailAutoSaveStatus("saving");
    try {
      const payload = {
        name: String(selectedEmailTemplateName || "").trim() || "Untitled",
        subject: String(emailSubject || "").trim() || "A Message from Listernaut",
        fromName: resolveEmailFromName(emailFromName, defaultEmailFromName),
        html,
        unlayerDesign: design || null,
      };
      const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.marketingTemplates}/${encodeURIComponent(templateId)}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(data?.message || data?.error || "Auto-save failed");
      setEmailAutoSaveStatus("saved");
      setTimeout(() => setEmailAutoSaveStatus(""), 2000);
    } catch (error) {
      console.error("Email auto-save failed:", error);
      setEmailAutoSaveStatus("error");
      setTimeout(() => setEmailAutoSaveStatus(""), 3000);
    }
  }, [
    apiBaseUrl,
    apiFetchJson,
    defaultEmailFromName,
    emailFromName,
    emailHtml,
    emailSubject,
    emailUnlayerDesign,
    orgReady,
    resolveEmailFromName,
    routes.marketingTemplates,
    selectedEmailTemplateId,
    selectedEmailTemplateName,
    settings,
  ]);

  const triggerEmailAutoSave = useCallback(() => {
    if (!selectedEmailTemplateId) return;
    setEmailAutoSaveStatus("pending");
    clearTimeout(emailAutoSaveTimerRef.current);
    emailAutoSaveTimerRef.current = setTimeout(() => doEmailAutoSaveToDb(), 2000);
  }, [doEmailAutoSaveToDb, selectedEmailTemplateId]);

  useEffect(() => {
    if (!emailEditorReady) return;
    const editor = emailEditorRef.current?.editor;
    if (!editor || typeof editor.addEventListener !== "function") return;

    const onDesignUpdated = () => {
      if (skipEmailDesignAutoSaveRef.current) return;
      triggerEmailAutoSave();
    };

    editor.addEventListener("design:updated", onDesignUpdated);
    return () => {
      clearTimeout(emailAutoSaveTimerRef.current);
      if (typeof editor.removeEventListener === "function") {
        editor.removeEventListener("design:updated", onDesignUpdated);
      }
    };
  }, [emailEditorReady, triggerEmailAutoSave]);

  useEffect(() => {
    if (!selectedEmailTemplateId) return;
    if (emailSkipAutoSaveRef.current) {
      emailSkipAutoSaveRef.current = false;
      return;
    }
    triggerEmailAutoSave();
  }, [emailFromName, emailSubject, selectedEmailTemplateId, selectedEmailTemplateName, triggerEmailAutoSave]);

  const applyRecipientTokens = useCallback((input, recipient) => {
    const src = String(input || "");
    const bidderName = String(recipient?.bidderName || recipient?.username || recipient?.email || "there").trim() || "there";
    const auctionUrl = buildDefaultAuctionUrl() || "";
    const emailEncoded = encodeURIComponent(String(recipient?.email || "").trim());
    return src
      .replaceAll("{{bidderName}}", bidderName)
      .replaceAll("{{auctionUrl}}", auctionUrl)
      .replaceAll("{{email}}", emailEncoded);
  }, [buildDefaultAuctionUrl]);

  const fetchEmailTemplates = useCallback(async () => {
    if (!orgReady) return;
    setEmailTemplatesBusy(true);
    try {
      const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.marketingTemplates}`);
      if (!res.ok) throw new Error(`Failed to load templates (${res.status})`);
      setEmailTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      toastOpen(error?.message || "Failed to load templates", "error");
    } finally {
      setEmailTemplatesBusy(false);
    }
  }, [apiBaseUrl, apiFetchJson, orgReady, routes.marketingTemplates, toastOpen]);

  useEffect(() => {
    if (!emailOpen) return;
    void fetchEmailTemplates();
  }, [emailOpen, fetchEmailTemplates]);

  useEffect(() => {
    const selectedId = String(selectedEmailTemplateId || "").trim();
    if (!selectedId) {
      if (selectedEmailTemplateName) setSelectedEmailTemplateName("");
      return;
    }
    const template = (emailTemplates || []).find((row) => String(row.id) === selectedId);
    if (!template) {
      setSelectedEmailTemplateId("");
      setSelectedEmailTemplateName("");
      return;
    }
    setSelectedEmailTemplateName(String(template.name || "").trim());
  }, [emailTemplates, selectedEmailTemplateId, selectedEmailTemplateName]);

  const resetEmailComposer = useCallback(() => {
    setEmailPreviewHtml("");
    setEmailTab("compose");
    setSelectedEmailTemplateId("");
    setSelectedEmailTemplateName("");
    setCreateEmailTemplateName("");
    setCreateEmailTemplateBaseId("");
    setCreateEmailTemplateOpen(false);
    setEmailUnlayerDesign(emptyEmailDesign);
    setPendingEmailDesign(emptyEmailDesign);
    setEmailEditorReady(false);
  }, [emptyEmailDesign]);

  const handleSelectEmailTemplate = useCallback((templateId) => {
    setSelectedEmailTemplateId(templateId);
    if (!templateId) {
      setSelectedEmailTemplateName("");
      setEmailFromName(defaultEmailFromName);
      setPendingEmailDesign(emptyEmailDesign);
      setEmailUnlayerDesign(emptyEmailDesign);
      return;
    }
    const template = (emailTemplates || []).find((row) => String(row.id) === String(templateId));
    if (!template) return;
    emailSkipAutoSaveRef.current = true;
    setSelectedEmailTemplateName(String(template.name || "").trim());
    setEmailSubject(String(template.subject || ""));
    setEmailFromName(resolveEmailFromName(template.fromName, defaultEmailFromName));
    setEmailHtml(String(template.html || ""));
    const nextDesign = template?.unlayerDesign || emptyEmailDesign;
    setEmailUnlayerDesign(nextDesign);
    setPendingEmailDesign(nextDesign);
    setEmailPreviewHtml("");
  }, [defaultEmailFromName, emailTemplates, emptyEmailDesign, resolveEmailFromName]);

  const createEmailTemplateFromCurrent = useCallback(async ({ name, baseTemplateId = "" } = {}) => {
    if (!orgReady) return;
    const nextTemplateName = String(name || "").trim();
    if (!nextTemplateName) {
      toastOpen("Template name is required.", "error");
      return;
    }

    let out = null;
    let html = "";
    let subjectForTemplate = String(emailSubject || "").trim() || "A Message from Listernaut";
    let fromNameForTemplate = resolveEmailFromName(emailFromName, defaultEmailFromName);
    const baseTemplate = String(baseTemplateId || "").trim();
    if (baseTemplate) {
      const template = (emailTemplates || []).find((row) => String(row.id) === baseTemplate);
      if (!template) {
        toastOpen("Base template not found.", "error");
        return;
      }
      html = String(template.html || "").trim();
      out = { html, design: template?.unlayerDesign || null };
      subjectForTemplate = String(template.subject || "").trim() || subjectForTemplate;
      fromNameForTemplate = resolveEmailFromName(template.fromName, defaultEmailFromName);
      setEmailSubject(subjectForTemplate);
      setEmailFromName(fromNameForTemplate);
      setEmailHtml(html);
      const baseDesign = template?.unlayerDesign || emptyEmailDesign;
      setEmailUnlayerDesign(baseDesign);
      setPendingEmailDesign(baseDesign);
      setEmailPreviewHtml("");
    } else {
      out = await exportEmailFromBuilder();
      html = String(out?.html || "").trim();
    }

    if (!html) {
      toastOpen("Email HTML is empty.", "error");
      return;
    }

    setEmailTemplatesBusy(true);
    try {
      const payload = {
        name: nextTemplateName,
        subject: subjectForTemplate,
        fromName: fromNameForTemplate,
        html,
        unlayerDesign: out?.design || emailUnlayerDesign || null,
      };
      const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.marketingTemplates}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(data?.message || data?.error || `Create template failed (${res.status})`);
      setCreateEmailTemplateName("");
      setCreateEmailTemplateBaseId("");
      setCreateEmailTemplateOpen(false);
      setEmailSubject(subjectForTemplate);
      toastOpen("Template created.", "success");
      await fetchEmailTemplates();
      const createdId = String(data?.id || "").trim();
      if (createdId) {
        setSelectedEmailTemplateId(createdId);
        setSelectedEmailTemplateName(String(data?.name || nextTemplateName).trim());
      }
    } catch (error) {
      toastOpen(error?.message || "Failed to create template", "error");
    } finally {
      setEmailTemplatesBusy(false);
    }
  }, [
    apiBaseUrl,
    apiFetchJson,
    defaultEmailFromName,
    emailFromName,
    emailSubject,
    emailTemplates,
    emailUnlayerDesign,
    emptyEmailDesign,
    exportEmailFromBuilder,
    fetchEmailTemplates,
    orgReady,
    resolveEmailFromName,
    routes.marketingTemplates,
    settings,
    toastOpen,
  ]);

  const updateSelectedEmailTemplate = useCallback(async () => {
    if (!orgReady) return;
    const templateId = String(selectedEmailTemplateId || "").trim();
    if (!templateId) {
      toastOpen("Select a template to update.", "error");
      return;
    }
    const templateName = String(selectedEmailTemplateName || "").trim();
    if (!templateName) {
      toastOpen("Template name cannot be empty.", "error");
      return;
    }

    const out = await exportEmailFromBuilder();
    const html = String(out?.html || "").trim();
    if (!html) {
      toastOpen("Email HTML is empty.", "error");
      return;
    }

    setEmailTemplatesBusy(true);
    try {
      const payload = {
        name: templateName,
        subject: String(emailSubject || "").trim() || "A Message from Listernaut",
        fromName: resolveEmailFromName(emailFromName, defaultEmailFromName),
        html,
        unlayerDesign: out?.design || emailUnlayerDesign || null,
      };
      const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.marketingTemplates}/${encodeURIComponent(templateId)}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(data?.message || data?.error || `Update template failed (${res.status})`);
      await fetchEmailTemplates();
      setSelectedEmailTemplateName(String(data?.name || templateName).trim());
      toastOpen("Template updated.", "success");
    } catch (error) {
      toastOpen(error?.message || "Failed to update template", "error");
    } finally {
      setEmailTemplatesBusy(false);
    }
  }, [
    apiBaseUrl,
    apiFetchJson,
    defaultEmailFromName,
    emailFromName,
    emailSubject,
    emailUnlayerDesign,
    exportEmailFromBuilder,
    fetchEmailTemplates,
    orgReady,
    resolveEmailFromName,
    routes.marketingTemplates,
    selectedEmailTemplateId,
    selectedEmailTemplateName,
    settings,
    toastOpen,
  ]);

  const deleteSelectedEmailTemplate = useCallback(async () => {
    if (!orgReady) return;
    const templateId = String(selectedEmailTemplateId || "").trim();
    if (!templateId) {
      toastOpen("Select a template to delete.", "error");
      return;
    }
    const ok = window.confirm("Delete this template? This cannot be undone.");
    if (!ok) return;

    setEmailTemplatesBusy(true);
    try {
      const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.marketingTemplates}/${encodeURIComponent(templateId)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(data?.message || data?.error || `Delete template failed (${res.status})`);
      setSelectedEmailTemplateId("");
      setSelectedEmailTemplateName("");
      await fetchEmailTemplates();
      toastOpen("Template deleted.", "success");
    } catch (error) {
      toastOpen(error?.message || "Failed to delete template", "error");
    } finally {
      setEmailTemplatesBusy(false);
    }
  }, [apiBaseUrl, apiFetchJson, fetchEmailTemplates, orgReady, routes.marketingTemplates, selectedEmailTemplateId, toastOpen]);

  const requestEmailPreview = useCallback(async () => {
    setEmailPreviewBusy(true);
    try {
      const out = await exportEmailFromBuilder();
      const fallbackUser = (allFilteredUsers || []).find((row) => isEmail(normalize(row?.email || row?.id)));
      const sample =
        computeEmailRecipients()?.[0] ||
        (fallbackUser
          ? {
            email: normalize(fallbackUser?.email || fallbackUser?.id),
            bidderName: fallbackUser?.bidderName || "",
            username: fallbackUser?.username || "",
          }
          : { email: "", bidderName: "", username: "" });
      const previewHtml = applyRecipientTokens(out?.html || getComposedEmailHtml(), sample);
      setEmailPreviewHtml(previewHtml || out?.html || getComposedEmailHtml());
    } catch (error) {
      toastOpen(error?.message || "Preview failed", "error");
      setEmailPreviewHtml("");
    } finally {
      setEmailPreviewBusy(false);
    }
  }, [
    allFilteredUsers,
    applyRecipientTokens,
    computeEmailRecipients,
    exportEmailFromBuilder,
    getComposedEmailHtml,
    isEmail,
    normalize,
    toastOpen,
  ]);

  const sendEmailNow = useCallback(async () => {
    if (!orgReady) return;
    setEmailBusy(true);
    try {
      const recipients = computeEmailRecipients().filter((recipient) => isEmail(recipient?.email));
      if (!recipients.length) throw new Error("No valid recipients.");
      if (!String(emailSubject || "").trim()) throw new Error("Subject is required.");
      const out = await exportEmailFromBuilder();
      const htmlBase = out?.html || getComposedEmailHtml();
      const hasHtml = String(htmlBase || "").trim().length > 0;
      const hasText = String(emailText || "").trim().length > 0;
      if (!hasHtml && !hasText) throw new Error("Provide HTML or Text body.");
      const placeholderBody = `${String(emailSubject || "")}\n${String(emailText || "")}\n${String(htmlBase || "")}`;
      const wantsShippingPlaceholder = /(?:https?:\/\/)?(?:www\.)?shippingplaceholder\.com/i.test(placeholderBody);
      const wantsPickupPlaceholder = /(?:https?:\/\/)?(?:www\.)?pickupplaceholder\.com/i.test(placeholderBody);
      const auctionIdForSend = emailMode === "auction"
        ? String(selectedAuctionId || recipients?.[0]?.auctionId || "").trim()
        : "";
      if ((wantsShippingPlaceholder || wantsPickupPlaceholder) && emailMode !== "auction") {
        throw new Error("Pickup and shipping emails can only be sent from the auction email page.");
      }
      if ((wantsShippingPlaceholder || wantsPickupPlaceholder) && !auctionIdForSend) {
        throw new Error("Select an auction before sending a pickup or shipping email.");
      }
      const queue = [...recipients];
      const CONCURRENCY = 4;
      let sent = 0;
      let failed = 0;
      const sentEmails = [];
      const sendEndpoint = `${apiBaseUrl}${routes.marketingSendEmail}`;

      const runWorker = async () => {
        while (queue.length) {
          const recipient = queue.shift();
          if (!recipient) continue;

          const subject = applyRecipientTokens(emailSubject, recipient);
          const html = hasHtml ? applyRecipientTokens(htmlBase, recipient) : "";
          const text = hasText ? applyRecipientTokens(emailText, recipient) : "";
          const recipientAuctionId = String(recipient?.auctionId || auctionIdForSend || "").trim();
          const recipientRegistrationId = String(recipient?.registrationId || "").trim();
          const payload = {
            to: [
              {
                email: recipient.email,
                auctionId: recipientAuctionId,
                registrationId: recipientRegistrationId,
                bidderName: String(recipient?.bidderName || "").trim(),
                username: String(recipient?.username || "").trim(),
              },
            ],
            subject,
            html,
            text,
            fromName: resolveEmailFromName(emailFromName, defaultEmailFromName),
            customArgs: {
              listernaut_email_type: "hibid",
              hibid_mode: emailMode,
              hibid_subscription_id: subscriptionId || "",
              hibid_auction_id: recipientAuctionId || "",
              hibid_registration_id: recipientRegistrationId || "",
            },
            auctionId: recipientAuctionId || "",
            registrationId: recipientRegistrationId || "",
          };

          try {
            const { res, data } = await apiFetchJson(sendEndpoint, {
              method: "POST",
              body: JSON.stringify(payload),
            });
            if (!res.ok || data?.ok === false) throw new Error(data?.error || data?.message || `Send failed (${res.status})`);
            sent += 1;
            sentEmails.push(normalize(recipient.email));
          } catch {
            failed += 1;
          }
        }
      };

      const workers = Array.from({ length: Math.min(CONCURRENCY, recipients.length) }, () => runWorker());
      await Promise.all(workers);

      if (sentEmails.length > 0) {
        const sentSet = new Set(sentEmails);
        const sentAtIso = new Date().toISOString();
        const sentAtMs = Date.now();

        if (emailMode === "users") {
          setUserRows((prev) =>
            (prev || []).map((row) => {
              const email = normalize(row?.email || row?.id);
              if (!sentSet.has(email)) return row;
              return { ...row, lastEmailSentAt: sentAtIso };
            })
          );
          invalidateUsersCache();
        }

        if (selectedAuctionId) {
          setAuctionRegs((prev) =>
            (prev || []).map((row) => {
              const email = normalize(row?.bidderEmail || row?.email);
              if (!sentSet.has(email)) return row;
              const chargebackPatch = wantsShippingPlaceholder
                ? {
                  chargebackEmailSent: true,
                  chargebackEmailSentAt: sentAtIso,
                  chargebackEmailSentAtMs: sentAtMs,
                }
                : {};
              return {
                ...row,
                emailSentAt: sentAtIso,
                emailTracking: { ...(row?.emailTracking || {}), sentAt: sentAtIso },
                ...chargebackPatch,
              };
            })
          );
          invalidateAuctionRegsCache(selectedAuctionId);
        }
      }

      toastOpen(`Sent ${sent}${failed ? ` (failed ${failed})` : ""}.`, "success");
      setEmailOpen(false);
    } catch (error) {
      toastOpen(error?.message || "Failed to send email", "error");
    } finally {
      setEmailBusy(false);
    }
  }, [
    apiBaseUrl,
    apiFetchJson,
    applyRecipientTokens,
    computeEmailRecipients,
    defaultEmailFromName,
    emailFromName,
    emailMode,
    emailSubject,
    emailText,
    exportEmailFromBuilder,
    getComposedEmailHtml,
    invalidateAuctionRegsCache,
    invalidateUsersCache,
    isEmail,
    normalize,
    orgReady,
    resolveEmailFromName,
    routes.marketingSendEmail,
    selectedAuctionId,
    setAuctionRegs,
    setUserRows,
    subscriptionId,
    toastOpen,
  ]);

  const openEmailForUser = useCallback((row) => {
    const email = normalize(row?.email || row?.id);
    if (!isEmail(email)) return;
    const name = String(row?.bidderName || row?.username || email).trim();
    setEmailMode("users");
    setEmailRecipients([{ email, bidderName: row?.bidderName || "", username: row?.username || "" }]);
    setEmailRecipientScope("selected");
    setEmailSubject(`Hi ${name} - quick update`);
    setEmailFromName(defaultEmailFromName);
    setEmailHtml(
      `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;color:#111827">
        <p>Hi ${name},</p>
        <p>Quick update from ${subscriptionName || "our team"}.</p>
        <p style="margin-top:24px">Thanks,<br/>${subscriptionName || "Team"}</p>
      </div>`
    );
    setEmailText(`Hi ${name},\n\nQuick update from ${subscriptionName || "our team"}.\n\nThanks,\n${subscriptionName || "Team"}`);
    resetEmailComposer();
    setEmailOpen(true);
  }, [defaultEmailFromName, isEmail, normalize, resetEmailComposer, subscriptionName]);

  const openEmailForUserSelection = useCallback((rows) => {
    const list = (rows || [])
      .map((row) => {
        const email = normalize(row?.email || row?.id);
        if (!isEmail(email)) return null;
        return { email, bidderName: row?.bidderName || "", username: row?.username || "" };
      })
      .filter(Boolean);
    if (!list.length) return;

    setEmailMode("users");
    setEmailRecipients(list);
    setEmailRecipientScope("selected");
    setEmailSubject(`Quick update from ${subscriptionName || "our team"}`);
    setEmailFromName(defaultEmailFromName);
    setEmailHtml(
      `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;color:#111827">
        <p>Hi {{bidderName}},</p>
        <p>Quick update from ${subscriptionName || "our team"}.</p>
        <p style="margin-top:24px">Thanks,<br/>${subscriptionName || "Team"}</p>
      </div>`
    );
    setEmailText(`Hi {{bidderName}},\n\nQuick update from ${subscriptionName || "our team"}.\n\nThanks,\n${subscriptionName || "Team"}`);
    resetEmailComposer();
    setEmailOpen(true);
  }, [defaultEmailFromName, isEmail, normalize, resetEmailComposer, subscriptionName]);

  const openEmailForLoadedUsers = useCallback(() => {
    if (selectedFilteredUsers?.length) {
      openEmailForUserSelection(selectedFilteredUsers);
      return;
    }
    openEmailForUserSelection(allFilteredUsers || []);
  }, [allFilteredUsers, openEmailForUserSelection, selectedFilteredUsers]);

  const openEmailForAuctionSelection = useCallback((rows) => {
    const list = (rows || [])
      .map((row) => {
        const email = normalize(row?.bidderEmail || row?.email);
        if (!isEmail(email)) return null;
        return {
          email,
          bidderName: row?.bidderName || "",
          username: row?.username || "",
          auctionId: selectedAuctionId || row?.auctionId || "",
          auctionName: selectedAuctionLabel || row?.auctionName || "",
          registrationId: String(row?.registrationId || row?.id || "").trim(),
        };
      })
      .filter(Boolean);

    if (!list.length) return;
    setEmailMode("auction");
    setEmailRecipients(list);
    setEmailRecipientScope("selected");
    setChargebackEmailFulfillmentFilter("all");
    setChargebackEmailAlreadySentFilter("all");
    setChargebackEmailWonLotFilter("all");
    setEmailSubject(`Update about ${selectedAuctionLabel || "your auction"}`);
    setEmailFromName(defaultEmailFromName);
    setEmailHtml(
      `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;color:#111827">
        <p>Hi {{bidderName}},</p>
        <p>Quick update about <b>${selectedAuctionLabel || "this auction"}</b>.</p>
        <p>Link: <a href="{{auctionUrl}}" target="_blank" rel="noreferrer">{{auctionUrl}}</a></p>
        <p style="margin-top:24px">Thanks,<br/>${subscriptionName || "Team"}</p>
      </div>`
    );
    setEmailText(`Hi {{bidderName}},\n\nQuick update about ${selectedAuctionLabel || "this auction"}.\nAuction: {{auctionUrl}}\n\nThanks,\n${subscriptionName || "Team"}`);
    resetEmailComposer();
    setEmailOpen(true);
  }, [
    defaultEmailFromName,
    isEmail,
    normalize,
    resetEmailComposer,
    selectedAuctionId,
    selectedAuctionLabel,
    setChargebackEmailAlreadySentFilter,
    setChargebackEmailFulfillmentFilter,
    setChargebackEmailWonLotFilter,
    subscriptionName,
  ]);

  const saveShippingText = useCallback(async () => {
    if (!orgReady || shippingTextSaving) return;
    const nextText = String(shippingTextDraft || "").trim();
    if (!nextText) {
      toastOpen("Shipping text cannot be empty.", "error");
      return;
    }
    setShippingTextSaving(true);
    try {
      const { res, data } = await apiFetchJson(`${apiBaseUrl}${routes.orgProfile}`, {
        method: "POST",
        body: JSON.stringify({ shippingPolicyText: nextText }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Save failed (${res.status})`);
      const saved = String(data?.shippingPolicyText || nextText).trim() || defaultShippingPolicyText;
      setShippingTextDraft(saved);
      setShippingTextSaved(saved);
      setShippingTextOpen(false);
      toastOpen("Shipping text saved.", "success");
    } catch (error) {
      toastOpen(error?.message || "Failed to save shipping text", "error");
    } finally {
      setShippingTextSaving(false);
    }
  }, [
    apiBaseUrl,
    apiFetchJson,
    defaultShippingPolicyText,
    orgReady,
    routes.orgProfile,
    shippingTextDraft,
    shippingTextSaving,
    toastOpen,
  ]);

  return {
    createEmailTemplateBaseId,
    createEmailTemplateName,
    createEmailTemplateOpen,
    defaultEmailFromName,
    deleteSelectedEmailTemplate,
    emailAutoSaveStatus,
    emailBusy,
    emailEditorRef,
    emailMode,
    emailFromName,
    emailSubject,
    emailOpen,
    emailPreviewBusy,
    emailPreviewHtml,
    emailRecipientTotalForDisplay,
    emailSingleUserTarget,
    emailTab,
    emailTemplates,
    emailTemplatesBusy,
    getComposedEmailHtml,
    handleEmailEditorReady,
    handleSelectEmailTemplate,
    openEmailForAuctionSelection,
    openEmailForLoadedUsers,
    openEmailForUser,
    requestEmailPreview,
    saveShippingText,
    selectedEmailTemplateId,
    selectedEmailTemplateName,
    sendEmailNow,
    setCreateEmailTemplateBaseId,
    setCreateEmailTemplateName,
    setCreateEmailTemplateOpen,
    setEmailFromName,
    setEmailOpen,
    setEmailSubject,
    setEmailTab,
    setSelectedEmailTemplateName,
    setShippingTextDraft,
    setShippingTextOpen,
    setShippingTextSaved,
    shippingTextDirty,
    shippingTextDraft,
    shippingTextOpen,
    shippingTextSaved,
    shippingTextSaving,
    updateSelectedEmailTemplate,
    createEmailTemplateFromCurrent,
  };
}
